import React, { useLayoutEffect } from 'react';

import {
  CustomizedButton,
  CustomizedDialog,
  CustomizedTypography
} from '@components/Customized';

import { Box, MenuItem, Select } from '@material-ui/core';
import { Create } from '@material-ui/icons';
import useStyles from '../useStyles';
import OutlinedTextField, {
  CurrencyFormatInput
} from '@routes/Common/OutlinedTextField';
import { UploadedDrawingFileType } from '@modules/request';
import { ServiceTypes } from '../RequestForQuotationUpload';

import {
  selectCastingOptions,
  selectCNCOptions,
  selectMoldingOptions,
  selectPrintingOptions,
  selectSheetMetalOptions
} from './SelectOptionsTypes';

import { requestEnumToString } from '@utils/enumToString';
import {
  Controller,
  useForm,
  useFormContext as useFormContextFromContainer
} from 'react-hook-form';

import {
  CapaQuotationDetailedMaterialType,
  CapaQuotationDetailedServiceType,
  CapaQuotationMaterial,
  CapaQuotationRecommendation,
  CapaQuotationServiceType
} from '@generated/graphql';

import OptionRecommendationBox from './OptionRecommendationBox';

interface Props extends ServiceTypes {
  open: boolean;
  selectedFileInfo: UploadedDrawingFileType | undefined;
  uploadedFilesError: string[];
  onClose: () => void;
  handleBatchUpdateRfqFile: (payload: UploadedDrawingFileType) => void;
}

const OptionSelectBatchDialog = ({
  serviceType,
  detailedServiceType,
  selectedFileInfo,
  uploadedFilesError,
  open,
  onClose,
  handleBatchUpdateRfqFile
}: Props) => {
  const methods = useForm<UploadedDrawingFileType>();

  const { clearErrors } = useFormContextFromContainer();

  const { control, errors, watch, reset, setValue, handleSubmit } = methods;

  const classes = useStyles();

  const hasDetailedMaterial = [
    CapaQuotationServiceType.Cnc,
    CapaQuotationServiceType.Inj
  ].includes(serviceType);

  const material = watch('material');

  const detailedMaterial = hasDetailedMaterial
    ? watch('detailedMaterial')
    : undefined;

  const isRecommendServiceType =
    detailedServiceType === CapaQuotationDetailedServiceType.Recommend;

  const hasEtc = material?.includes('ETC') || detailedMaterial?.includes('ETC');

  const hasRecommendation =
    material?.includes('RECOMMEND') ||
    detailedMaterial?.includes('RECOMMEND') ||
    isRecommendServiceType;

  const selectedMaterialRecommend =
    material === CapaQuotationMaterial.Recommend;

  const materialOptions =
    serviceType === 'CNC'
      ? selectCNCOptions.material
      : serviceType === 'PRINTING'
      ? selectPrintingOptions.material[detailedServiceType]
      : serviceType === 'INJ'
      ? selectMoldingOptions.material
      : serviceType === 'SHEET_METAL'
      ? selectSheetMetalOptions.material[detailedServiceType]
      : serviceType === 'CASTING'
      ? selectCastingOptions.material[detailedServiceType]
      : [];

  const detailedMaterialOptions =
    serviceType === 'CNC'
      ? selectCNCOptions.detailedMaterial[material as CapaQuotationMaterial]
      : serviceType === 'INJ'
      ? selectMoldingOptions.detailedMaterial[material as CapaQuotationMaterial]
      : undefined;

  const onSubmit = handleSubmit((data) => {
    for (const fileId of uploadedFilesError) {
      clearErrors(fileId);
    }

    const isEtc =
      data?.material === CapaQuotationMaterial.Etc ||
      data?.detailedMaterial === CapaQuotationDetailedMaterialType.Etc;

    const materialRecommend =
      data?.material === CapaQuotationMaterial.Recommend;

    const detailedMaterialRecommend =
      data?.detailedMaterial === CapaQuotationDetailedMaterialType.Recommend;

    if (isRecommendServiceType) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        material: CapaQuotationMaterial.Recommend
      });

      return onClose();
    }

    if (isEtc) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        recommendation: undefined
      });

      return onClose();
    }

    if (materialRecommend) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        detailedMaterial: undefined,
        etc: undefined
      });

      return onClose();
    }

    if (detailedMaterialRecommend) {
      handleBatchUpdateRfqFile({
        ...selectedFileInfo,
        ...data,
        etc: undefined
      });

      return onClose();
    }

    handleBatchUpdateRfqFile({ ...selectedFileInfo, ...data });
    return onClose();
  });

  useLayoutEffect(() => {
    reset({ ...selectedFileInfo });
    // eslint-disable-next-line
  }, [reset]);

  return (
    <CustomizedDialog
      closeButton
      open={open}
      onClose={onClose}
      maxWidth={490}
      borderRadius={8}
      color="primary"
      icon={<Create />}
      title="가공 옵션 일괄적용"
    >
      <Box className={classes['rfq-option-select-dialog']}>
        <Box className="option-field top">
          <CustomizedTypography
            variant="body1"
            fontWeight="500"
            color="textPrimary"
            gutterBottoms={8}
          >
            해당 가공 옵션은 모든 도면에 적용됩니다.
          </CustomizedTypography>
        </Box>
        {!isRecommendServiceType && (
          <Box className="option-field">
            <CustomizedTypography
              variant="body2"
              color="textPrimary"
              gutterBottoms={4}
            >
              {hasDetailedMaterial ? '재료 대분류*' : '재료*'}
            </CustomizedTypography>

            <Controller
              defaultValue={''}
              control={control}
              name="material"
              rules={{ required: true }}
              render={(render) => (
                <Select
                  {...render}
                  classes={{
                    select: classes['option-select']
                  }}
                  variant="outlined"
                  fullWidth
                  displayEmpty
                  error={errors['material'] ? true : false}
                  onChange={(e) => {
                    setValue('detailedMaterial', '');
                    return render.onChange(e);
                  }}
                >
                  <MenuItem value="" disabled>
                    {hasDetailedMaterial ? '재료 대분류' : '재료'}
                  </MenuItem>
                  {materialOptions?.map((option, index) => (
                    <MenuItem
                      classes={{ root: 'option-menu-item' }}
                      key={index}
                      value={option}
                    >
                      {requestEnumToString(option)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors['material'] && (
              <span className="errorMessage">
                {hasDetailedMaterial ? '재료 대분류' : '재료'}를 선택해주세요.
              </span>
            )}
          </Box>
        )}

        {hasDetailedMaterial &&
          !selectedMaterialRecommend &&
          !isRecommendServiceType && (
            <Box className="option-field">
              <CustomizedTypography
                variant="body2"
                color="textPrimary"
                gutterBottoms={4}
              >
                재료 소분류*
              </CustomizedTypography>

              <Controller
                defaultValue={''}
                control={control}
                name="detailedMaterial"
                rules={{ required: true }}
                error={errors['detailedMaterial'] ? true : false}
                render={(render) => (
                  <Select
                    {...render}
                    classes={{
                      select: classes['option-select']
                    }}
                    variant="outlined"
                    fullWidth
                    displayEmpty
                    disabled={!material}
                  >
                    <MenuItem value="" disabled>
                      재료 소분류
                    </MenuItem>
                    {detailedMaterialOptions?.map((option, index) => (
                      <MenuItem
                        classes={{ root: 'option-menu-item' }}
                        key={index}
                        value={option}
                      >
                        {requestEnumToString(option)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors['detailedMaterial'] && (
                <span className="errorMessage">
                  재료 소분류를 선택해주세요.
                </span>
              )}
            </Box>
          )}

        {hasEtc && (
          <Box className="option-field">
            <CustomizedTypography
              variant="body2"
              color="textPrimary"
              gutterBottoms={4}
            >
              기타 재료*
            </CustomizedTypography>

            <Controller
              control={control}
              name="etc"
              defaultValue=""
              rules={{ required: true }}
              render={(render) => (
                <OutlinedTextField
                  {...render}
                  fullWidth
                  placeholder="기타 재료를 입력해주세요"
                  align="left"
                  size="large"
                  error={errors['etc'] ? true : false}
                />
              )}
            />

            {errors['etc'] && (
              <span className="errorMessage">기타 재료를 입력해주세요.</span>
            )}
          </Box>
        )}

        {hasRecommendation && (
          <Box className="option-field">
            <CustomizedTypography variant="body2" color="textPrimary">
              재료 특성*
            </CustomizedTypography>
            <CustomizedTypography
              variant="subtitle2"
              color="textSecondary"
              gutterBottoms={8}
            >
              선택한 특성에 맞게 파트너가 재료를 추천합니다. (최대 2개 선택)
            </CustomizedTypography>

            <Controller
              control={control}
              name="recommendation"
              defaultValue={[]}
              rules={{
                validate: (value) => {
                  return value.length !== 0;
                }
              }}
              render={(render) => (
                <React.Fragment>
                  <OptionRecommendationBox
                    {...render}
                    methods={methods}
                    serviceType={serviceType}
                    detailedServiceType={detailedServiceType}
                  />

                  {(render.value as CapaQuotationRecommendation[]).includes(
                    CapaQuotationRecommendation.Etc
                  ) && (
                    <span className="errorMessage">
                      보기 이외의 특성이 필요한 경우, 상세 요청사항에
                      입력해주세요
                    </span>
                  )}

                  {(render.value as CapaQuotationRecommendation[]).includes(
                    CapaQuotationRecommendation.DecideAfterConsulting
                  ) && (
                    <span className="errorMessage">
                      상담 후 결정 선택 시 모든 특성 선택지가 비활성화 됩니다
                    </span>
                  )}

                  {errors['recommendation'] && (
                    <span className="errorMessage">
                      제조에 필요한 재료 특성을 선택해주세요.
                    </span>
                  )}
                </React.Fragment>
              )}
            />
          </Box>
        )}

        <Box className="option-field bottom">
          <CustomizedTypography
            variant="body2"
            color="textPrimary"
            gutterBottoms={4}
          >
            수량*
          </CustomizedTypography>

          <Controller
            control={control}
            name="quantity"
            defaultValue=""
            rules={{ required: true, validate: (value) => Number(value) !== 0 }}
            render={(render) => (
              <OutlinedTextField
                {...render}
                fullWidth
                placeholder="수량 입력"
                align="left"
                size="large"
                error={errors['quantity'] ? true : false}
                InputProps={{
                  inputComponent: CurrencyFormatInput
                }}
                onChange={(e) => {
                  if (e.target.value?.length > 10) {
                    return;
                  }
                  render.onChange(e.target.value as any);
                }}
              />
            )}
          />

          {errors['quantity']?.type === 'required' && (
            <span className="errorMessage">수량을 입력해주세요.</span>
          )}

          {errors['quantity']?.type === 'validate' && (
            <span className="errorMessage">
              0을 제외한 숫자만 입력해주세요.
            </span>
          )}
        </Box>

        <Box className="button-group">
          <CustomizedButton
            fullWidth
            color="default"
            variant="contained"
            size="large"
            onClick={onClose}
          >
            취소하기
          </CustomizedButton>
          <CustomizedButton
            fullWidth
            color="primary"
            variant="contained"
            size="large"
            onClick={onSubmit}
          >
            저장하기
          </CustomizedButton>
        </Box>
      </Box>
    </CustomizedDialog>
  );
};

export default OptionSelectBatchDialog;
